import { observer } from 'mobx-react';
import { reaction } from "mobx";
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Typography, Tooltip, useTheme } from '@mui/material';
import { Button, IconButton, Container, Box, Grid, Divider } from '@mui/material';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { NextStepButton } from '/@/app/components/UserFlow/NextStepButton';

import ReplayIcon from '@mui/icons-material/Replay';

import { Spinner } from "/@/app/components/Spinner.jsx";
import { SectionList } from "./SectionList.js";
import { Session } from "./Session.js";
import { Linkify } from "/@/app/linkify";
import { AppContext } from '/@/app/AppContext';
import { StepInfoBlock } from '/@/app/components/StepInfoBlock';
import { AudioPlayer } from '/@/app/components/AudioPlayer';
//
import { debugSettingsState } from "/@/app/components/debug/DebugSettingsState.js";
import { useApi} from '/@/app/Api';


const MainQuestionPanel = styled(Box)(({ theme }) => ({
    backgroundColor: '#f5f7fb',
    ...theme.typography.body2,

    borderRadius: theme.shape.borderRadius * 1.5,
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
    },
    //
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));


const PrevAnswerDetails = observer(({ question }) => {
    const theme = useTheme();
    if (question.answer === null) {
        return <div className="replace-answer-notice"></div>
    }
    if (question.type !== "common") {
        return <div className="replace-answer-notice"></div>
    }
    const prevQuestion = {
        "yes": (<Box sx={{ display: "inline", color: theme.palette.success.main }}>Да</Box>),
        "no": (<Box sx={{ display: "inline", color: theme.palette.error.main }}>Нет</Box>),
        "i_dont_know": (<Box sx={{ display: "inline", color: theme.palette.secondary.main }}>Не знаю</Box>),
    }[question.answer.answer];

    return (
        <div className="replace-answer-notice">
            Прошлый ответ: {prevQuestion}
        </div>
    )
});


const RepeatQuestionsBar = observer(({ session, sx }) => {
    const answered = session.countAnswered;
    const visible = session.countVisible;
    const iDontKnow = session.countIDontKnow;
    const left = visible - answered;
    const theme = useTheme();

    return (
        <Box sx={{
            display: "flex",
            flexFlow: "column nowrap",
            gap: 1,
            justifyContent: "center",
            border: "1px solid",
            borderColor: "rgba(140, 78, 210, 0.2)",   // transpaernt theme.palette.primary.main,
            borderRadius: 2,
            p: 0.5, pr: 3, pl: 1.5,
            mt: 1,
            maxWidth: "350px",
            ...sx,
        }}>
            <Typography variant="body2" sx={{ textAlign: "center", color: theme.palette.primary.main }}>
                вернуться к вопросам:
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexFlow: "row wrap",
                    gap: 2,
                    justifyContent: "center",
                }}
            >
                <Tooltip title="Пройти неотвеченные вопросы">
                    <Box sx={{textAlign: "center"}}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => session.startUnanswered()}
                            size="small"
                            sx={{ textAlign: "center" }}
                        >
                            <RadioButtonUncheckedIcon /> ({left})
                        </Button>
                        <Box sx={{ fontSize: "14px", color: theme.palette.primary.main }}>
                            неотвеченные
                        </Box>
                    </Box>
                </Tooltip>
                <Tooltip title='Пройти вопросы с ответом "я не знаю"'>
                    <Box sx={{textAlign: "center"}}>
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => session.startIDontKnow()
                            }
                            sx={{ textAlign: "center" }}
                        >
                            <HelpRoundedIcon /> ({iDontKnow})
                        </Button>
                        <Box sx={{ fontSize: "14px", color: theme.palette.secondary.main }}>
                            "я не знаю"
                        </Box>
                    </Box>
                </Tooltip>
                <Tooltip title='Пересмотреть все вопросы с самого начала'>
                    <Box sx={{textAlign: "center"}}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => session.startOver()}
                            sx={{ textAlign: "center" }}
                        >
                            <ReplayIcon />
                        </Button>
                        <Box sx={{ fontSize: "14px", color: theme.palette.primary.main }}>
                            все
                        </Box>
                    </Box>
                </Tooltip>
            </Box>
        </Box>);
});


const QuestionBottomBar = observer(({ session, audioHelp }) => {
    const { help } = useContext(AppContext).bootstrapData;
    const { i18n } = useTranslation();

    return (
        <Container maxWidth="sm" sx={{
            display: "flex",
            flexFlow: "row wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
        }}>
            {audioHelp &&
                <AudioPlayer audioSrc={help.sounds['questions-'+i18n.language]} sx={{
                    float: "left",
                    mt: "15px",
                }} />
            }
            {/* show below the question; but also can be shown inside. */}
            {session.currentQuestion !== null &&
                <RepeatQuestionsBar session={session} />
            }
        </Container>
    );
})

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1, ml: 2 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    props.value,
                )}%`}</Typography>
            </Box>
        </Box>
    );
}
LinearProgressWithLabel.propTypes = {
    // Value between 0 and 100.
    value: PropTypes.number.isRequired,
};


const CurrentQuestionChoices = observer(({ question, unsavedChoice, setUnsavedChoice, sx }) => {
    const handleChange = (event) => {
        setUnsavedChoice(parseInt(event.target.value));
    };

    return (
        <Box sx={{
            display: "flex",
            flexFlow: "row nowrap",
            justifyContent: "center",
            alignItems: "center",
            ...sx,
        }}>
            <RadioGroup
                value={unsavedChoice}
                onChange={handleChange}
                sx={{ mt: 1 }}
            >
                {question.choices.map((choice, idx) => (
                    <FormControlLabel
                        key={idx}
                        value={choice.permanentId}
                        control={<Radio/>}
                        label={choice.caption}
                        sx={{
                            mb: 0.5,
                            "span": {lineHeight: "1.1"},
                            textAlign: "left",
                        }}
                    />
                ))}
            </RadioGroup>
        </Box>
    );
});


const CurrentQuestion = observer(({ session }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const question = session.currentQuestion;
    const { help } = useContext(AppContext).bootstrapData;
    const { i18n } = useTranslation();
    const [unsavedChoice, setUnsavedChoice] = useState(question?.groupChoice ?? 0);  // use 0 instead of null/undefinded, so RadioGroup will always get some value

    const questionsLeft = session.countVisible - session.countAnswered;

    if (session.loading) {
        // Centered spinner
        return (
            <Container maxWidth="sm" sx={{ textAlign: "center", p: 0 }}>
                <Spinner />
            </Container>
        );
    }

    let progress = Math.round((session.countAnswered / session.countVisible) * 100);
    let { section: currentSection, questionIndex} = session.sectionList.findByQuestion(question);

    //
    // handle choices
    //
    // FIXME it adds a reaction on every render
    reaction(() => session.currentQuestion, (question) => {
        setUnsavedChoice(question?.groupChoice ?? 0);
    });
    const saveChoice = () => {
        if (unsavedChoice) {
            question.saveGroup(unsavedChoice);
        }
    }

    return (
        <>
            <Box className="questionnaire-current-question"
                 sx={{
                     // Rules for different screens:
                     // 1. Preserve minimum height for all cases.
                     // The question can be scrollable inside, but try to avoid it if possible.
                     // 2. Keep the height fixed across questions,
                     // so the buttons will be in the same place.
                     //
                     // Cases:
                     // - mobile: use around 100% height, but with a limit.
                     // - rotated mobile: should be readable. Use a fixed height, prevent scrolling inside.
                     // - desktop: try to use full screen height, but not too much.
                     // - small height desktop / zoomed interface: use a fixed height, prevent scrolling inside.
                     //
                     // It can be discussed with the team / UX specialists later.
                     [theme.breakpoints.up('xs')]: {
                         height: theme.custom.roughHeightBelowBar.sx,
                         minHeight: "400px",  // for landscape layout
                         maxHeight: "min(80vh, 720px)",  // for bigger mobile screens
                     },
                     [theme.breakpoints.up('md')]: {
                         height: "min(85vh, 720px)",  // limit for desktop screens
                         p: theme.spacing(2)
                     },
                 }}
            >
                <Box className="question-progress">
                    <LinearProgressWithLabel value={progress} />
                    {session.answeringMode !== "all" ? (
                        <div className="answering-mode-notice">
                            {session.answeringMode === "unanswered" ?
                                <Box sx={{ backgroundColor: theme.palette.warning.main, p: 1, pl: 2, pr: 2, borderRadius: 2 }}>Неотвеченные вопросы:</Box>: null}
                            {session.answeringMode === "iDontKnow" ?
                                <Box sx={{ backgroundColor: theme.palette.info.main, p: 1, pl: 2, pr: 2, borderRadius: 2 }}>С ответом "Я не знаю":</Box>: null}
                        </div>) : <></>
                    }
                </Box>

                <MainQuestionPanel className="current-question" sx={{
                    position: "relative"
                }}>
                    {question === null ?
                        <Box>
                            {questionsLeft > 0 ?
                                <Box>
                                    <Typography variant="body1" sx={{ textAlign: "center" }}>
                                        Осталось вопросов: {questionsLeft}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => session.startUnanswered()}
                                        sx={{ textAlign: "center", mt: 1 }}
                                    >
                                        Пройти неотвеченные вопросы
                                    </Button>
                                </Box>
                                :
                                <Box>
                                    <Typography variant="body1" sx={{ textAlign: "center" }}>
                                        Поздравляем! Вы ответили на все вопросы.
                                    </Typography>
                                    <NextStepButton to="/lab-tests/" />
                                </Box>
                            }
                            <Divider sx={{ mt: 5, mb: 2 }} />
                            <Box sx={{ display: 'flex', flexFlow: "row nowrap", justifyContent: "center" }}>
                                <RepeatQuestionsBar session={session} sx={{ mt: 2, }} />
                            </Box>
                        </Box>
                        :
                        <>
                            {currentSection ? (
                                <Box className="question-header">
                                    <AudioPlayer audioSrc={question.soundLink} sx={{ float: "right", ml: 2 }} size="25px" hideCaption={true} />
                                    {currentSection.name}:
                                    <span style={{whiteSpace: "nowrap"}}> {questionIndex+1} из {currentSection.countVisible}</span>
                                </Box>
                            ): null}



                            <Box
                                className="question-contents"
                                sx={{
                                    [theme.breakpoints.up('xs')]: {
                                        pl: 1,
                                        pr: 1,
                                    },
                                    [theme.breakpoints.up('sm')]: {
                                        pl: 3,
                                        pr: 3,
                                    }
                                }}
                            >
                                {/* Centered + scroll on overflow */}
                                <Box className="question-content-inner"
                                     sx={{
                                         // // Adaptive font size
                                         fontSize: (question.caption.length > 150) ? "18px" : "26px",
                                         lineHeight: (question.type === "common" ? "1.3" : "1.2"),
                                     }}
                                >
                                    <Linkify text={question.caption} />

                                    {question.type === "single_choice_group" &&
                                        <CurrentQuestionChoices
                                            question={question}
                                            unsavedChoice={unsavedChoice}
                                            setUnsavedChoice={setUnsavedChoice}
                                            sx={{ mt: 1 }}
                                        />
                                    }
                                </Box>
                            </Box>


                            <div className="question-answer">
                                <div className="answer-info-line"
                                     sx={{
                                         // fixed height to prevent flickering on hide/show
                                         height: theme.spacing(2)
                                     }}
                                >
                                    <div className={classNames(
                                        "answer-back", {
                                            "answer-back-hidden": session.historyStack.length === 0,
                                        })}
                                    >
                                        <IconButton onClick={() => {session.goBack()}}>
                                            <ChevronLeftIcon />Назад
                                        </IconButton>
                                    </div>
                                    <PrevAnswerDetails question={question} />
                                    <Spinner
                                        hidden={!question.savingState}
                                        size={40}
                                    />
                                </div>
                                <div className="answer-buttons">
                                    {question.type === "common" ?
                                        <>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="large"
                                                onClick={() => question.saveAnswer("yes")}
                                                sx={{
                                                    color: theme.palette.common.white,
                                                    width: "80px",  // same as "No"
                                                }}
                                            >Да</Button>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="large"
                                                onClick={() => question.saveAnswer("i_dont_know")}
                                            >Не знаю</Button>
                                            <Button
                                                size="large"
                                                variant="contained"
                                                color="error"
                                                onClick={() => question.saveAnswer("no")}
                                                sx={{
                                                    width: "80px",  // same as "No"
                                                }}
                                            >Нет</Button>
                                        </>
                                        :
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="large"
                                            onClick={saveChoice}
                                            {...unsavedChoice === null ? {disabled: true} : {}}
                                            sx={{
                                                color: theme.palette.common.white,
                                            }}
                                        >Ответить</Button>
                                    }
                                </div>
                            </div>
                        </>
                    }
                </MainQuestionPanel>
            </Box>

            {/* goes AFTER the window-height dependent box: */}
            <QuestionBottomBar className="question-bottom-bar" session={session} audioHelp={true} />
            <Box sx={{ mt: 5 }}>
                <StepInfoBlock text={ help.texts['questions_active-footer-'+i18n.language] } />
            </Box>
        </>
    )
});


const NavigationQuestion = observer(({ question, index }) => {
    // Setup autoscroll
    const questionRef = useRef(null);
    useEffect(() => {
        if (question.navCurrent && questionRef.current) {
            questionRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [question.navCurrent]);

    let statusComponent;
    switch (question.type) {
        case "common":
            if (question.answer !== null) {
                statusComponent = {
                    "yes": <CheckCircleRoundedIcon color="success"></CheckCircleRoundedIcon>,
                    "no": <CheckCircleRoundedIcon sx={{ color:"#ff8b8b" }}></CheckCircleRoundedIcon>,
                    "i_dont_know": <HelpRoundedIcon color="secondary"></HelpRoundedIcon>,
                }[question.answer.answer];
            } else {
                statusComponent = <MoreHorizIcon sx={{color: "transparent"}}></MoreHorizIcon>
            }
            break;
        case "single_choice_group":
            if (question.groupChoice !== null) {
                statusComponent = {
                    "yes": <CheckCircleRoundedIcon color="success"></CheckCircleRoundedIcon>,
                    // "NO" for "none of the above" answer.
                    "no": <CheckCircleRoundedIcon sx={{ color:"#ff8b8b" }}></CheckCircleRoundedIcon>,
                }[question.answer.answer];
            } else {
                statusComponent = <MoreHorizIcon sx={{color: "transparent"}}></MoreHorizIcon>
            }
            break;
    }


    let nameComponent;
    let _foundPrefix = (question.nav_prefix !== null && question.nav_prefix.prefix);
    const [foundPrefix, setFoundPrefix] = useState(_foundPrefix);
    reaction(() => debugSettingsState.questionsTocFormat, (format) => {
        if (format === "plain") {
            setFoundPrefix(false);
        } else {
            setFoundPrefix(_foundPrefix);
        }
    });

    if (foundPrefix) {
        nameComponent = (
            <>
                {question.nav_prefix.first
                    ? <div className="nav-q-caption caption-prefix">{question.nav_prefix.prefix}</div>
                    : <></>
                }
                <div className="nav-q-caption caption-tail">{statusComponent}<div>- {question.nav_prefix.tail}</div></div>
            </>
        )
    } else {
        nameComponent = (
            <div className={classNames("nav-q-caption caption-single", {
                "caption-single-gap": (
                    // used to divide from prefixed questions, but not the 1st element in the section
                    question.nav_prefix.first
                    && index!==0
                ),
            })}>
                {statusComponent}<div>{question.caption}</div>
            </div>
        );
    }

    return (
        <div
            ref={questionRef}
            className={classNames("nav-question", {
                "nav-question-current": question.navCurrent,
            })}
            onClick={() => {
                question.setNavCurrent();
                question.setIWantToBecomeCurrent();
            }}
        >
            {nameComponent}
        </div>
    )
});


const NavigationSectionHeader = observer(({ section }) => {
    return <div
        onClick={() => section.toggleNav()}
        className="section-nav-header"
    >
        <div className="section-caption">{section.name}</div>
        <div className="section-counts">
            <div className="count-answered">
                {section.countAnswered}
            </div>
            /
            <div className="count-visible">
                {section.countVisible}
            </div>
        </div>
        <div className="section-nav-toggle">
            <IconButton size="small">
                {section.navOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
        </div>
    </div>
});


const NavigationSection = observer(({ section }) => {
    // Setup autoscroll
    const sectionRef = useRef(null);
    useEffect(() => {
        if (
            section.navOpen
            && sectionRef.current
            // && !isElementInViewport(sectionRef.current)
        ) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start'});
        }
    }, [section.navOpen]);

    return (
        <div
            ref={sectionRef}
            className={classNames('section-nav', {
                "section-nav-opened": section.navOpen,
                "section-nav-hidden": section.countVisible === 0,
            })}
        >
            <NavigationSectionHeader section={section} />
            <div className="section-nav-contents">
                {section.questions.map((question, idx) => (
                    <NavigationQuestion key={idx} question={question} index={idx} />
                ))}
            </div>
        </div>
    )
});


const Navigation = observer(({ session }) => {
    const theme = useTheme();
    if (session.loading) {
        return <></>;
    }
    return (
        <Box
            className="questionnaire questions-navigation"
            sx={{
                overflowY: "auto",
                [theme.breakpoints.up('xs')]: { p: 0 },
                [theme.breakpoints.up('sm')]: { p: 1 },
                [theme.breakpoints.up('md')]: { p: 2 },
            }}
        >
            {session.sectionList.sections.map(section => (
                <NavigationSection key={section.name} section={section} />
            ))}
        </Box>
    )
});


//
// Demo widget here, not actually in use
//
const Questionnaire = ({ panelId }) => {
    const api=useApi();

    const sections = new SectionList(panelId, api);
    const state = new Session(sections);
    sections.fetchData();

    return (
        <>
            <CurrentQuestion session={state} />
            <Navigation session={state} />
        </>
    );
};


Questionnaire.SectionList = SectionList;
Questionnaire.Session = Session;
Questionnaire.CurrentQuestion = CurrentQuestion;
Questionnaire.Navigation = Navigation;

export default Questionnaire;
